const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

class WorkflowQuestion {
  constructor(element) {
    this.element = element;
    this.key = element.dataset.questionKey;
    this.index = Number(element.dataset.questionIndex);
    this.dependencies = JSON.parse(element.dataset.questionDependencies);
    this.inputs = Array.from(element.querySelectorAll("input[type='radio']"));
    this.answerValues = this.inputs.map(input => input.value);
  }

  selectedValue() {
    return this.inputs.find(input => input.checked)?.value;
  }

  onAnswer(callback) {
    this.inputs.forEach(input => {
      input.addEventListener('change', () => callback(input.value));
    });
  }

  clear() {
    this.inputs.forEach(input => { input.checked = false; });
  }

  setDisabled(disabled) {
    this.inputs.forEach(input => { input.disabled = disabled; });
  }

  setRequired(required) {
    this.inputs.forEach(input => { input.required = required; });
  }

  setVisible(visible) {
    this.element.hidden = !visible;
  }

  focus() {
    const heading = this.element.querySelector('h4');
    heading.setAttribute('tabindex', '-1');
    heading.focus();
  }

  setQuestionNumber(number, total) {
    this.element.querySelector('[data-workflow-question-number]').textContent = number;
    this.element.querySelector('[data-workflow-question-total]').textContent = total;
  }
}

// Evaluates dependencies in authored order, producing a path through the
// applicable questions and the exact minimum and maximum counts for how many
// questions there are.
class WorkflowQuestionPath {
  constructor(questions, baselineAnswers) {
    this.questions = questions;
    this.baselineAnswers = baselineAnswers;
    this.dependencyKeysFromIndex = questions.map((_, index) => {
      const keys = questions
        .slice(index)
        .flatMap(question => Object.keys(question.dependencies));
      return Array.from(new Set(keys)).sort();
    });
  }

  dependenciesMet(question, answers) {
    return Object.entries(question.dependencies).every(([key, value]) => answers[key] === value);
  }

  calculate(answers) {
    const availableAnswers = {...this.baselineAnswers};

    return this.questions.filter(question => {
      if (!this.dependenciesMet(question, availableAnswers)) return false;

      if (hasOwn(answers, question.key)) {
        availableAnswers[question.key] = answers[question.key];
      }
      return true;
    });
  }

  bounds(currentQuestion, ordinal, answers) {
    const availableAnswers = {...this.baselineAnswers};

    this.questions
      .filter(question => question.index < currentQuestion.index)
      .forEach(question => {
        if (this.dependenciesMet(question, availableAnswers) && hasOwn(answers, question.key)) {
          availableAnswers[question.key] = answers[question.key];
        }
      });

    const currentAnswers = hasOwn(answers, currentQuestion.key) ?
      [answers[currentQuestion.key]] :
      currentQuestion.answerValues;
    const cache = new Map();
    const remainingBounds = currentAnswers.map(answer => {
      const branchAnswers = {...availableAnswers, [currentQuestion.key]: answer};
      return this.remainingBounds(currentQuestion.index + 1, branchAnswers, answers, cache);
    });

    return {
      minimum: ordinal + Math.min(...remainingBounds.map(bound => bound.minimum)),
      maximum: ordinal + Math.max(...remainingBounds.map(bound => bound.maximum)),
    };
  }

  remainingBounds(index, availableAnswers, selectedAnswers, cache) {
    if (index >= this.questions.length) return {minimum: 0, maximum: 0};

    const dependencyState = this.dependencyKeysFromIndex[index].map(key => [
      key,
      hasOwn(availableAnswers, key) ? availableAnswers[key] : null,
    ]);
    const cacheKey = `${index}:${JSON.stringify(dependencyState)}`;
    if (cache.has(cacheKey)) return cache.get(cacheKey);

    const question = this.questions[index];
    let result;
    if (!this.dependenciesMet(question, availableAnswers)) {
      result = this.remainingBounds(index + 1, availableAnswers, selectedAnswers, cache);
    } else {
      const possibleAnswers = hasOwn(selectedAnswers, question.key) ?
        [selectedAnswers[question.key]] :
        question.answerValues;
      const branches = possibleAnswers.map(answer => {
        const branchAnswers = {...availableAnswers, [question.key]: answer};
        return this.remainingBounds(index + 1, branchAnswers, selectedAnswers, cache);
      });
      result = {
        minimum: 1 + Math.min(...branches.map(branch => branch.minimum)),
        maximum: 1 + Math.max(...branches.map(branch => branch.maximum)),
      };
    }

    cache.set(cacheKey, result);
    return result;
  }
}

// Owns the task submit button's disabled state and disabled-state tooltip.
class WorkflowTaskSubmitControl {
  constructor(form) {
    this.button = form.querySelector('[data-workflow-task-submit]');
    this.tooltipElement = form.querySelector('[data-workflow-task-submit-tooltip]');
    this.tooltip = null;

    if (this.tooltipElement) {
      this.tooltipElement.setAttribute('title', this.tooltipElement.dataset.tooltipTitle);
      if (window.jQuery && window.jQuery.fn.tooltip) {
        this.tooltip = window.jQuery(this.tooltipElement);
        this.tooltip.tooltip({delay: {show: 500, hide: 100}, placement: 'left'});
      }
    }
  }

  setDisabled(disabled) {
    if (!this.button) return;

    this.button.disabled = disabled;
    if (this.tooltip) {
      if (disabled) {
        this.tooltip.tooltip('enable');
      } else {
        this.tooltip.tooltip('hide');
        this.tooltip.tooltip('disable');
      }
    } else if (this.tooltipElement) {
      if (disabled) {
        this.tooltipElement.setAttribute('title', this.tooltipElement.dataset.tooltipTitle);
      } else {
        this.tooltipElement.removeAttribute('title');
      }
    }
  }
}

// Owns the transition state and animation between question cards.
class WorkflowQuestionAnimation {
  constructor(duration = 240) {
    this.duration = duration;
    this.running = false;
  }

  shouldAnimate(outgoingQuestions, incomingQuestions) {
    const reducedMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return (outgoingQuestions.length > 0 || incomingQuestions.length > 0) && !reducedMotion;
  }

  async transition(outgoingQuestions, incomingQuestions, updateQuestions) {
    if (!this.shouldAnimate(outgoingQuestions, incomingQuestions)) {
      updateQuestions();
      return;
    }

    this.running = true;
    try {
      const outgoingAnimations = outgoingQuestions.map(question =>
        question.element.animate(
          [
            {opacity: 1},
            {opacity: 0},
          ],
          {duration: this.duration, easing: 'ease-in-out', fill: 'forwards'},
        ));
      await Promise.all(outgoingAnimations.map(animation => animation.finished));

      updateQuestions();
      outgoingAnimations.forEach(animation => animation.cancel());

      const incomingAnimations = incomingQuestions.map(question =>
        question.element.animate(
          [
            {opacity: 0},
            {opacity: 1},
          ],
          {duration: this.duration, easing: 'ease-in-out'},
        ));
      await Promise.all(incomingAnimations.map(animation => animation.finished));
    } finally {
      this.running = false;
    }
  }
}

// Coordinates answer state, the current question, transitions, and UI controls.
class WorkflowQuestionWizard {
  constructor(element) {
    this.element = element;
    this.form = element.closest('form');
    this.readOnly = element.dataset.readOnly === 'true';
    this.questions = Array.from(element.querySelectorAll('[data-workflow-question]'))
      .map(question => new WorkflowQuestion(question));

    const workflowAnswers = JSON.parse(element.dataset.workflowAnswers);
    this.baselineAnswers = {...workflowAnswers};
    this.questions.forEach(question => { delete this.baselineAnswers[question.key]; });

    this.answers = {};
    this.questions.forEach(question => {
      const value = question.selectedValue();
      if (value !== undefined) this.answers[question.key] = value;
    });

    this.questionPath = new WorkflowQuestionPath(this.questions, this.baselineAnswers);
    this.submitControl = new WorkflowTaskSubmitControl(this.form);
    this.animation = new WorkflowQuestionAnimation();

    this.questions.forEach(question => {
      question.onAnswer((value) => this.answerChanged(question, value));
    });

    this.path = this.questionPath.calculate(this.answers);
    this.currentQuestion = this.nextUnansweredQuestion(this.path) || this.path[this.path.length - 1];
    this.render();
  }

  answerChanged(question, value) {
    const previousValue = this.answers[question.key];
    this.answers[question.key] = value;

    if (previousValue !== value) {
      this.questions
        .filter(candidate => candidate.index > question.index)
        .forEach(candidate => {
          delete this.answers[candidate.key];
          candidate.clear();
        });
    }

    this.updateCurrentQuestion(question);
  }

  async updateCurrentQuestion(answeredQuestion) {
    if (this.animation.running) return;

    this.submitControl.setDisabled(true);

    const previousVisibleQuestions = this.visibleQuestions();
    const path = this.questionPath.calculate(this.answers);
    const currentQuestion = this.nextUnansweredQuestion(path, answeredQuestion.index) ||
      path[path.length - 1];
    const visibleQuestions = this.visibleQuestions(path, currentQuestion);
    const outgoingQuestions = previousVisibleQuestions.filter(question => !visibleQuestions.includes(question));
    const incomingQuestions = visibleQuestions.filter(question => !previousVisibleQuestions.includes(question));

    previousVisibleQuestions.forEach(question => question.setDisabled(true));

    try {
      await this.animation.transition(
        outgoingQuestions,
        incomingQuestions,
        () => {
          this.path = path;
          this.currentQuestion = currentQuestion;
          this.render();
        },
      );
    } finally {
      this.render(true);
    }
  }

  nextUnansweredQuestion(path, afterIndex = -1) {
    return path.find(question =>
      question.index > afterIndex && !hasOwn(this.answers, question.key));
  }

  visibleQuestions(path = this.path, currentQuestion = this.currentQuestion) {
    const currentIndex = path.indexOf(currentQuestion);
    return currentIndex < 0 ? [] : path.slice(0, currentIndex + 1);
  }

  render(moveFocus = false) {
    this.path = this.questionPath.calculate(this.answers);
    if (this.currentQuestion && !this.path.includes(this.currentQuestion)) {
      this.currentQuestion = this.path[0];
    }

    const visibleQuestions = this.visibleQuestions();
    const visibleQuestionSet = new Set(visibleQuestions);

    this.questions.forEach(question => {
      const isVisible = visibleQuestionSet.has(question);
      const enableInputs = !this.readOnly && !this.animation.running && isVisible;

      question.setVisible(isVisible);
      question.setDisabled(!enableInputs);
      question.setRequired(enableInputs);
    });

    visibleQuestions.forEach((question, index) => this.renderCounter(question, index));

    const allApplicableQuestionsAnswered = this.path.every(question => hasOwn(this.answers, question.key));
    this.submitControl.setDisabled(!allApplicableQuestionsAnswered);

    if (moveFocus && this.currentQuestion) {
      this.currentQuestion.focus();
    }
  }

  renderCounter(question, questionIndex) {
    const ordinal = questionIndex + 1;
    const {minimum, maximum} = this.questionPath.bounds(
      question,
      ordinal,
      this.answers,
    );
    const total = minimum === maximum ? `${minimum}` : `${minimum}-${maximum}`;

    question.setQuestionNumber(ordinal, total);
  }
}

export const initWorkflowQuestionWizards = () => {
  document.querySelectorAll('[data-workflow-question-wizard]').forEach(element => {
    new WorkflowQuestionWizard(element);
  });
};
